import React, { useEffect, useState } from "react";
import { Rocket, Check, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import api from "@/lib/api";
import { toast } from "sonner";

const LS_KEY = "gv_founding_joined";
const PERKS = ["Early access before public launch", "Founding Creator profile badge", "Bonus AI credits at launch", "A say in what we build next"];

// §17 "Imagine It. Build It." — founding-creators launch waitlist. Turns anonymous
// landing visitors into a warm launch-day audience.
export default function FoundingWaitlist() {
  const [count, setCount] = useState(null);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [joined, setJoined] = useState(() => { try { return localStorage.getItem(LS_KEY) === "1"; } catch { return false; } });

  useEffect(() => { api.get("/waitlist/founding").then(({ data }) => setCount(data.count || 0)).catch(() => setCount(0)); }, []);

  const join = async () => {
    const e = email.trim();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(e)) { toast.error("Please enter a valid email"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/waitlist/founding", { email: e, role: "creator" });
      setCount(data.count);
      setJoined(true);
      try { localStorage.setItem(LS_KEY, "1"); } catch {}
      toast.success(data.already ? "You're already on the list! 🚀" : "You're in — welcome, founding creator! 🚀");
    } catch (err) { toast.error(err.response?.data?.detail || "Something went wrong"); }
    finally { setBusy(false); }
  };

  return (
    <section className="relative z-10 max-w-5xl mx-auto px-5 py-14" data-testid="founding-waitlist">
      <div className="relative overflow-hidden rounded-3xl border border-[#3b9eff]/30 bg-gradient-to-br from-[#1f6feb]/15 via-[#7c3aed]/8 to-transparent p-8 sm:p-12">
        <div className="grid md:grid-cols-2 gap-8 items-center">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#3b9eff]/20 text-[#8fc6ff] text-xs font-bold mb-4">
              <Rocket className="w-3.5 h-3.5" /> IMAGINE IT. BUILD IT.
            </div>
            <h2 className="font-display text-3xl sm:text-4xl font-bold mb-3">Become a founding creator</h2>
            <p className="text-white/70 mb-5">Get in before everyone else. Founding creators shape GameView Studio and launch their first games with the community.</p>
            <ul className="space-y-2">
              {PERKS.map((pk) => (
                <li key={pk} className="flex items-center gap-2 text-sm text-white/85"><Star className="w-4 h-4 text-amber-400 fill-amber-400 shrink-0" /> {pk}</li>
              ))}
            </ul>
          </div>
          <div className="rounded-2xl bg-black/30 border border-white/10 p-6">
            {count !== null && (
              <div className="mb-4 text-center">
                <div className="text-4xl font-black text-white tabular-nums" data-testid="founding-count">{count.toLocaleString()}</div>
                <p className="text-xs text-white/50">creators already on the list</p>
              </div>
            )}
            {joined ? (
              <div className="text-center py-3" data-testid="founding-joined">
                <div className="w-12 h-12 rounded-full bg-emerald-500/20 grid place-items-center mx-auto mb-3"><Check className="w-6 h-6 text-emerald-400" /></div>
                <p className="font-semibold text-emerald-400">You're on the list!</p>
                <p className="text-sm text-white/60 mt-1">We'll email you the moment we open the doors.</p>
              </div>
            ) : (
              <div className="space-y-3">
                <Input value={email} onChange={(e) => setEmail(e.target.value)} type="email" placeholder="you@email.com"
                  onKeyDown={(e) => e.key === "Enter" && join()} className="h-11 bg-white/5 border-white/15" data-testid="founding-email" />
                <Button onClick={join} disabled={busy} size="lg"
                  className="w-full rounded-full bg-gradient-to-r from-[#1f6feb] to-[#3b9eff] font-bold" data-testid="founding-join-btn">
                  <Rocket className="w-5 h-5" /> Claim my founding spot
                </Button>
                <p className="text-[11px] text-white/40 text-center">No spam. Just your invite and launch updates.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
